import { useContext } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'gatsby-plugin-react-intl';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';

import Container from '../../Core/Container';
import RichText from '../../Core/RichText';
import { Context } from '../../Providers/Blade';
import Blade from '../Blade';
import Link from '../../Core/Link';
import { isNotEmpty } from '../../../utils';

import {
  LegalButton,
  Paragraph,
  Ulist,
  Olist,
  ParagraphContainer,
  StyledSup,
  StyledSpan,
} from './styles';

// this will be removed when all legal blades moves over to the paragraph content
const bodyStyle = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <Paragraph>{children}</Paragraph>,
    [BLOCKS.UL_LIST]: (_, children) => <Ulist>{children}</Ulist>,
    [BLOCKS.OL_LIST]: (_, children) => <Olist>{children}</Olist>,
    [INLINES.HYPERLINK]: (node, children) => {
      const { data } = node;
      return (
        <Link to={data.uri} fontSize="inherit">
          {children}
        </Link>
      );
    },
  },
};

const LegalDetails = ({ data }) => {
  if (!data) return null;
  const intl = useIntl();
  const {
    state: { legalState },
    updateLegalState,
  } = useContext(Context);
  const {
    id,
    component,
    linkText,
    bodyCopy,
    introductionBody,
    asteriskBody,
    daggerBody,
    sectionSignBody,
    numbersBody,
    footerBody,
  } = data;

  const prependSymbol = (index, symbol) => {
    switch (symbol) {
      case 'asterisk':
        return (
          <StyledSpan id={symbol}>
            <span className="sr-only">Footnote</span>*
          </StyledSpan>
        );
      case 'dagger':
        return (
          <StyledSup id={symbol}>
            <span className="sr-only">Footnote</span>
            {['†', '‡'][index]}
          </StyledSup>
        );
      case 'section-sign':
        return (
          <StyledSup id={symbol}>
            <span className="sr-only">Footnote</span>§
          </StyledSup>
        );
      case 'number':
        return (
          <StyledSup id={`${symbol}-${index + 1}`}>
            <span className="sr-only">Footnote</span>
            {`${index + 1}`}
          </StyledSup>
        );
      default:
        return '';
    }
  };

  const formatCopy = (copy, index, key, symbol) => {
    const options = {
      renderNode: {
        [BLOCKS.PARAGRAPH]: (_, children) => {
          return isNotEmpty(children) ? (
            <Paragraph>{children}</Paragraph>
          ) : null;
        },
        [BLOCKS.UL_LIST]: (_, children) => <Ulist>{children}</Ulist>,
        [BLOCKS.OL_LIST]: (_, children) => <Olist>{children}</Olist>,
        [INLINES.HYPERLINK]: (node, children) => {
          const { data: nodeData } = node;
          return (
            <Link to={nodeData.uri} fontSize="inherit">
              {children}
            </Link>
          );
        },
      },
    };
    return (
      <ParagraphContainer key={key}>
        {prependSymbol(index, symbol)}
        <RichText document={copy} options={options} />
      </ParagraphContainer>
    );
  };

  return (
    <>
      <Container key={id} type="legal-details">
        <LegalButton
          id="legal-blade"
          isOpen={legalState}
          onClick={() => {
            updateLegalState();
          }}
          aria-label={
            intl.locale === 'en'
              ? `Open ${linkText} modal`
              : `Ouvrez ${linkText} modal`
          }
        >
          {linkText}
        </LegalButton>
      </Container>
      <Blade title={linkText} isOpen={legalState} component={component}>
        {bodyCopy && <RichText document={bodyCopy} options={bodyStyle} />}
        {introductionBody &&
          introductionBody.map((rte, index) => {
            return formatCopy(rte.paragraph, index, rte.id, '');
          })}
        {asteriskBody &&
          asteriskBody.map((rte, index) => {
            return formatCopy(rte.paragraph, index, rte.id, 'asterisk');
          })}
        {daggerBody &&
          daggerBody.map((rte, index) => {
            return formatCopy(rte.paragraph, index, rte.id, 'dagger');
          })}
        {sectionSignBody &&
          sectionSignBody.map((rte, index) => {
            return formatCopy(rte.paragraph, index, rte.id, 'section-sign');
          })}
        {numbersBody &&
          numbersBody.map((rte, index) => {
            return formatCopy(rte.paragraph, index, rte.id, 'number');
          })}
        {footerBody &&
          footerBody.map((rte, index) => {
            return formatCopy(rte.paragraph, index, rte.id, '');
          })}
      </Blade>
    </>
  );
};

LegalDetails.propTypes = {
  data: PropTypes.shape().isRequired,
};

export default LegalDetails;
